import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import {
  allAttributes,
  attributeHeader,
  clientEventAllowed,
  clientScreen,
  clientTransition,
  COOKIE,
  cookieAttributeLabels,
  cookieScenarios,
  cookieSummary,
  describeSession,
  flowTransition,
  formatClock,
  HOST_PREFIX_ACCEPTED,
  HOST_PREFIX_REFUSED,
  HOST_PREFIX_RULE,
  hostPrefixRefused,
  initialClient,
  initialFlow,
  initialStore,
  isLive,
  labAccounts,
  screenName,
  SESSION_ENDED,
  shortHash,
  shortToken,
  storeAvailable,
  storeTransition,
  type ClientAction,
  type ClientEvent,
  type CookieAttribute,
  type FlowAction,
  type StoreAction,
} from './session-models';

import './relationship-labs.css';
import './session-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
  className = '',
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
  className?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className={`rel-table cb-record-grid ${className}`} role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="cb-check">
      <input type="checkbox" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}
const SIMULATION = 'A simulation of the browser, the server and its sessions table.';

export function SessionFlowLab() {
  const [state, setState] = useState(initialFlow);
  const act = (action: FlowAction) => setState((current) => flowTransition(current, action));
  const latest = state.exchanges.at(-1);
  const tone =
    !latest || latest.title === 'Log in as Maya'
      ? 'neutral'
      : latest.status === 200
        ? 'success'
        : 'warning';
  return (
    <Lab
      title="How does the second request prove who sent it?"
      eyebrow="Session model · the second request"
      className="rel-lab session-lab"
    >
      <Predict>
        Log in as Maya, then load /tasks. What does the second request carry that tells the server
        it came from Maya, and what happens without it?
      </Predict>
      <div className="cb-controls">
        <Toggle
          label="Browser keeps the cookie"
          checked={state.keepCookie}
          onChange={() => act('toggle-keep')}
        />
      </div>
      <div className="cb-controls">
        <Button size="sm" onClick={() => act('log-in')}>
          Log in as Maya
        </Button>
        <Button size="sm" variant="secondary" onClick={() => act('load')}>
          Load /tasks
        </Button>
        <Button size="sm" variant="secondary" onClick={() => act('load-elsewhere')}>
          Load /tasks from another browser
        </Button>
        <Reset onClick={() => act('reset')} />
      </div>
      <Facts
        values={[
          ['Cookie in this browser', state.jar ? `${COOKIE}=${shortToken(state.jar)}` : '(none)'],
          ['Cookie in the other browser', '(none)'],
          ['Sessions the server holds for Maya', String(state.sessions.length)],
        ]}
      />
      <div className="cb-two">
        <Panel label={latest ? `Request · ${latest.title}` : 'Request'}>
          <pre aria-label="Request">
            {latest ? latest.request.join('\n') : '(nothing sent yet)'}
          </pre>
        </Panel>
        <Panel label="Response">
          <pre aria-label="Response">
            {latest ? latest.response.join('\n') : '(no response yet)'}
          </pre>
        </Panel>
      </div>
      <Panel label="Exchanges so far">
        <pre aria-label="Exchanges so far">
          {state.exchanges.length
            ? state.exchanges
                .map(
                  (exchange) =>
                    `${exchange.title}: ${exchange.request.some((line) => line.startsWith('cookie:')) ? 'cookie sent' : 'no cookie'} → ${exchange.status}${
                      exchange.response.some((line) => line.startsWith('set-cookie:'))
                        ? ' with set-cookie'
                        : ''
                    }`,
                )
                .join('\n')
            : '(none yet)'}
        </pre>
      </Panel>
      <Result tone={tone}>{state.message}</Result>
      <p className="rel-caption">
        {SIMULATION} A browser adds the cookie header by itself to every request to the site that
        set it, and fetch on the same origin does the same. HTTP itself remembers nothing between
        requests.
      </p>
    </Lab>
  );
}

export function CookieAttributesLab() {
  const [attributes, setAttributes] = useState(allAttributes);
  const results = cookieScenarios(attributes);
  const refused = hostPrefixRefused(attributes);
  const keys = Object.keys(cookieAttributeLabels) as CookieAttribute[];
  return (
    <Lab
      title="What does each attribute block?"
      eyebrow="Session model · cookie attributes"
      className="rel-lab session-lab"
    >
      <Predict>
        Switch off one attribute at a time. Which scenario changes each time, and does the change
        expose the token or break the page?
      </Predict>
      <div className="cb-controls" role="group" aria-label="Cookie attributes">
        {keys.map((key) => (
          <Toggle
            key={key}
            label={cookieAttributeLabels[key]}
            checked={attributes[key]}
            onChange={() => setAttributes((current) => ({ ...current, [key]: !current[key] }))}
          />
        ))}
        <Reset onClick={() => setAttributes(allAttributes())} />
      </div>
      <p className="rel-caption">{HOST_PREFIX_RULE}</p>
      <Panel label="Header the server sends at login">
        <pre aria-label="Set-Cookie header">{attributeHeader(attributes)}</pre>
      </Panel>
      <Facts
        values={[
          [
            '__Host- prefix',
            refused
              ? `${HOST_PREFIX_REFUSED} The rows show what each attribute does without the prefix.`
              : HOST_PREFIX_ACCEPTED,
          ],
        ]}
      />
      <Table
        label="Scenarios"
        columns={['Scenario', 'Result', 'What happens']}
        template="minmax(0, 1.2fr) minmax(0, 0.6fr) minmax(0, 1.7fr)"
        className="session-wide"
        rows={results.map((result) => [result.scenario, result.outcome, result.detail])}
      />
      <Result tone={results.every((result) => result.wanted) ? 'success' : 'warning'}>
        {cookieSummary(attributes)}
      </Result>
      <p className="rel-caption">
        {SIMULATION} Each row depends on one attribute, so it shows what that attribute blocks on
        its own.
      </p>
    </Lab>
  );
}

export function SessionStoreLab() {
  const [state, setState] = useState(initialStore);
  const act = (action: StoreAction) => setState((current) => storeTransition(current, action));
  const available = storeAvailable(state);
  const last = state.last;
  return (
    <Lab
      title="What happens to a session row?"
      eyebrow="Session model · the sessions table"
      className="rel-lab session-lab"
    >
      <Predict>
        Maya logs in, then logs out. Her browser kept a copy of the old cookie. What does the server
        answer when that copy is sent, and what changes if the clock runs out instead?
      </Predict>
      <div className="cb-controls">
        <Button size="sm" disabled={!available['maya-log-in']} onClick={() => act('maya-log-in')}>
          Maya logs in
        </Button>
        <Button size="sm" disabled={!available['ravi-log-in']} onClick={() => act('ravi-log-in')}>
          Ravi logs in
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!available['maya-log-out']}
          onClick={() => act('maya-log-out')}
        >
          Maya logs out
        </Button>
        <Button size="sm" variant="secondary" onClick={() => act('advance')}>
          Advance the clock 8 hours
        </Button>
      </div>
      <div className="cb-controls cb-secondary">
        <Button
          size="sm"
          variant="secondary"
          disabled={!available['old-cookie']}
          onClick={() => act('old-cookie')}
        >
          Send Maya&apos;s old cookie
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!available['copied-hash']}
          onClick={() => act('copied-hash')}
        >
          Use a copied token hash as a cookie
        </Button>
        <Reset onClick={() => act('reset')} />
      </div>
      <Facts
        values={[
          ['Clock, now()', formatClock(state.now)],
          [
            "Maya's old cookie",
            state.oldCookie ? `${COOKIE}=${shortToken(state.oldCookie.token)}` : '(not issued yet)',
          ],
        ]}
      />
      <Table
        label="sessions table"
        columns={['token_hash', 'user_id', 'expires_at', 'expires_at > now()']}
        className="session-rows"
        rows={state.rows.map((row) => [
          shortHash(row.token.hash),
          `${labAccounts[row.user].id} (${row.user})`,
          formatClock(row.expiresAt),
          isLive(row, state.now) ? 'true' : 'false',
        ])}
      />
      <div className="cb-two">
        <Panel label={last ? `Request · ${last.title}` : 'Request'}>
          <pre aria-label="Store request">
            {last ? last.request.join('\n') : '(nothing sent yet)'}
          </pre>
        </Panel>
        <Panel label="Server and response">
          <pre aria-label="Server and response">
            {last
              ? [...last.server, '', ...last.response].join('\n')
              : '(nothing has happened yet)'}
          </pre>
        </Panel>
      </div>
      <Result
        tone={
          last?.status === 401
            ? 'warning'
            : last?.status === 200 && last.title !== 'Maya logs in' && last.title !== 'Ravi logs in'
              ? 'success'
              : 'neutral'
        }
      >
        {state.message}
      </Result>
      <p className="rel-caption">
        {SIMULATION} The table stores only the SHA-256 hash of each token; the token itself exists
        only in the browser’s cookie. In this model a person with a live session logs out or waits
        for expiry before logging in again.
      </p>
    </Lab>
  );
}

const clientEvents: [ClientEvent, string][] = [
  ['open-valid', 'Open the page with a valid cookie'],
  ['open-none', 'Open the page without a cookie'],
  ['log-in-right', 'Log in with the right password'],
  ['log-in-wrong', 'Log in with a wrong password'],
];
export function AuthClientStatesLab() {
  const [state, setState] = useState(initialClient);
  const act = (action: ClientAction) => setState((current) => clientTransition(current, action));
  const signedOutNotice =
    state.session.kind === 'signed-out' && state.session.notice === SESSION_ENDED;
  const tone =
    state.session.kind === 'signed-in'
      ? state.errors.length
        ? 'warning'
        : 'success'
      : signedOutNotice
        ? 'success'
        : 'neutral';
  return (
    <Lab
      title="Which screen does the page show?"
      eyebrow="Session model · client states"
      className="rel-lab session-lab"
    >
      <Predict>
        Open the page with a valid cookie, then let a load return 401. Which screen appears with the
        401 listener on, and which with it off?
      </Predict>
      <div className="cb-controls">
        <Toggle
          label="401 listener"
          checked={state.listener}
          onChange={() => act('toggle-listener')}
        />
      </div>
      <div className="cb-controls">
        {clientEvents.map(([event, label]) => (
          <Button
            key={event}
            size="sm"
            variant={event.startsWith('open') ? 'primary' : 'secondary'}
            disabled={!clientEventAllowed(state, event)}
            onClick={() => act(event)}
          >
            {label}
          </Button>
        ))}
      </div>
      <div className="cb-controls cb-secondary">
        <Button
          size="sm"
          variant="secondary"
          disabled={!clientEventAllowed(state, 'load-401')}
          onClick={() => act('load-401')}
        >
          A load returns 401
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!clientEventAllowed(state, 'log-out')}
          onClick={() => act('log-out')}
        >
          Log out
        </Button>
        <Reset onClick={() => act('reset')} />
      </div>
      <Facts
        values={[
          ['Page shows', screenName(state.session)],
          ['session', describeSession(state.session)],
        ]}
      />
      <div className="cb-two">
        <Panel label="Screen">
          <pre aria-label="Screen">{clientScreen(state).join('\n')}</pre>
        </Panel>
        <Panel label="Requests and state changes">
          <pre aria-label="Client trace">{state.trace.join('\n')}</pre>
        </Panel>
      </div>
      <Result tone={tone}>{state.message}</Result>
      <p className="rel-caption">
        {SIMULATION} The page keeps one session value with three kinds: checking, signed out with a
        notice, or signed in with an account. Every screen is chosen from that value.
      </p>
    </Lab>
  );
}
